<template>
    <div class="miniMatchCardEnded miniMatchCard">
        <div class="miniMatchCard_teamInfo">
            <div class="miniMatchCard_dateInfo">
                <span class="miniMatchCard_time">
                    {{ dateDisplay }}
                </span>
            </div>
            <div class="miniMatchCard_resultInfo">
                <div class="miniMatchCard_teams">
                    <div class="miniMatchCard_teamRow">
                        <div class="miniMatchCard_teamNameContainer">
                            <picture>
                                <source
                                    v-bind:srcset="`${homeTeamLogo.webp}`"
                                    type="image/webp"
                                />
                                <img
                                    v-bind:src="`${homeTeamLogo.origin}`"
                                    alt="home team flag"
                                    class="miniMatchCard_flag"
                                />
                            </picture>
                            <div
                                class="miniMatchCard_teamName"
                                v-bind:class="{ 'miniMatchCard_teamName-win': isHomeWon }"
                            >
                                {{ homeTeam }}
                            </div>
                        </div>
                    </div>
                    <div class="miniMatchCard_teamRow">
                        <div class="miniMatchCard_teamNameContainer">
                            <picture>
                                <source
                                    v-bind:srcset="`${awayTeamLogo.webp}`"
                                    type="image/webp"
                                />
                                <img
                                    v-bind:src="`${awayTeamLogo.origin}`"
                                    alt="away team flag"
                                    class="miniMatchCard_flag"
                                />
                            </picture>
                            <div
                                class="miniMatchCard_teamName"
                                v-bind:class="{ 'miniMatchCard_teamName-win': isAwayWon }"
                            >
                                {{ awayTeam }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="miniMatchCard_resultStatus">
                    {{ matchStage }}
                </div>
                <div class="miniMatchCard_resultScore">
                    <div
                        class="miniMatchCard_score"
                        v-bind:class="{ 'miniMatchCard_score-win': isHomeWon }"
                    >
                        {{ homeScoreDisplay }}
                    </div>
                    <div
                        class="miniMatchCard_score"
                        v-bind:class="{ 'miniMatchCard_score-win': isAwayWon }"
                    >
                        {{ awayScoreDisplay }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, toRefs } from 'vue';
    import { getTournamentCenterTeamImage } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { formatDate } from '@/core/lib/date';
    import { getTranslated } from '@/core/lib/translationHelper';
    import type { IKnockoutEvent } from '@/interface/tournamentCenter';

    const props = defineProps<{
        event: IKnockoutEvent;
    }>();

    const { event } = toRefs(props);

    const homeTeam = computed(() => getTranslated(event.value.homeTeam?.teamName));
    const awayTeam = computed(() => getTranslated(event.value.awayTeam?.teamName));

    const homeTeamLogo = computed(() => getTournamentCenterTeamImage(event.value.homeTeam?.countryName));
    const awayTeamLogo = computed(() => getTournamentCenterTeamImage(event.value.awayTeam?.countryName));

    const dateDisplay = computed(() => (event.value.datetime ? formatDate(event.value.datetime, 'MM/dd') : ''));

    const finalScore = computed(() => {
        const { home: homeScore, away: awayScore } = event.value.regularEvent?.finalScore ?? { home: 0, away: 0 };
        const { home: extraTimeHomeScore, away: extraTimeAwayScore } = event.value.extraTimeEvent?.finalScore ?? { home: 0, away: 0 };
        return { home: homeScore + extraTimeHomeScore, away: awayScore + extraTimeAwayScore };
    });

    const penaltyScore = computed(() => ({
        home: event.value.penaltyEvent?.finalScore?.home ?? 0,
        away: event.value.penaltyEvent?.finalScore?.away ?? 0,
    }));

    const hasPenalty = computed(() => !!event.value.penaltyEvent?.finalScore);

    const totalScore = computed(() => ({
        home: finalScore.value.home + penaltyScore.value.home,
        away: finalScore.value.away + penaltyScore.value.away,
    }));

    const isHomeWon = computed(() => totalScore.value.home > totalScore.value.away);
    const isAwayWon = computed(() => totalScore.value.away > totalScore.value.home);

    const matchStage = computed(() => {
        if (event.value.extraTimeEvent?.finalScore && !hasPenalty.value) {
            return 'ET';
        }
        return hasPenalty.value ? 'FT(P)' : 'FT';
    });

    const homeScoreDisplay = computed(() => {
        if (hasPenalty.value) {
            return `${finalScore.value.home}(${penaltyScore.value.home})`;
        }
        return finalScore.value.home;
    });

    const awayScoreDisplay = computed(() => {
        if (hasPenalty.value) {
            return `${finalScore.value.away}(${penaltyScore.value.away})`;
        }
        return finalScore.value.away;
    });
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/MiniMatchCard";
</style>
